/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import once from 'lodash/once';
import classNames from 'classnames';

import {
  clientErrorTracker,
  trans,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';
import { Chip, ImageCarousel } from '@spotahome/ui-library';
import { DSIcon } from '@spotahome/ui-library/base';

import { OPEN_FULL_SCREEN_PHOTO_LIST } from '@spotahome/ui-library/src/tenant/components/utils/constants';

import { Mediator } from '@spotahome/soyuz-mediator';

import { SkeletonSlider } from '../Skeleton';

import {
  getImageTrackIndex,
  getVideoTrackIndex,
  getMapTrackIndex,
  findImageTrack,
  findVideoTrack
} from './utils/sliderAdapter';
import ImageMosaic from './ImageMosaic';

import './MainSliderItem.scss';

const trackEmptyImage = once(() => {
  clientErrorTracker.error({
    error: new Error('Listing images are missing'),
    source: 'Listing-MainSliderItem'
  });
});

const MainSliderItem = ({ tracks = [], onItemClick, deviceType }) => {
  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const createOpenSliderHandler = () => {
    const trackClickedId = getImageTrackIndex(tracks);

    onItemClick(trackClickedId);
  };

  const imageTrack = findImageTrack(tracks);
  const isImageTrackEmpty = !imageTrack?.items.length;
  const videoTrack = findVideoTrack(tracks);
  const videoTrackIndex = getVideoTrackIndex(tracks);
  const mapTrackIndex = getMapTrackIndex(tracks);

  useEffect(() => {
    if (isImageTrackEmpty) {
      trackEmptyImage();
    }
  }, [isImageTrackEmpty]);

  if (isImageTrackEmpty) {
    return <SkeletonSlider />;
  }

  const handleImageMosaicClick = () => {
    if (isListingSimplificationHeaderVariant) {
      Mediator.publish(OPEN_FULL_SCREEN_PHOTO_LIST);
    } else {
      createOpenSliderHandler();
    }
  };

  const hasThreeOrMoreImages = imageTrack?.items?.length >= 3;

  const imageCarouselClasses = classNames('main-slider-image__carousel', {
    'main-slider-image__carousel--listing-simplification':
      isListingSimplificationHeaderVariant
  });

  const hasVideoChip = Boolean(videoTrack);
  const hasMapChip = mapTrackIndex >= 0;

  return deviceType === 'desktop' && hasThreeOrMoreImages ? (
    <ImageMosaic
      imageTrack={imageTrack.items}
      onClick={handleImageMosaicClick}
      onVideoClick={
        hasVideoChip ? () => onItemClick(videoTrackIndex) : undefined
      }
      onMapClick={hasMapChip ? () => onItemClick(mapTrackIndex) : undefined}
    />
  ) : (
    <div
      data-test="main-slider-image-container"
      className="main-slider-image ga-slider-main-image"
    >
      <ImageCarousel
        onImageClick={createOpenSliderHandler}
        items={imageTrack.items}
        chevronStyle="big"
        preloadRange={0}
        keepHeightWhileLoading
        withBackgroundChevrons
        className={imageCarouselClasses}
      />
      {isListingSimplificationHeaderVariant && (
        <div className="main-slider-image__chips">
          {hasMapChip && (
            <Chip.Choice
              renderLeadingIcon={() => <DSIcon name="location_on_filled" />}
              className="main-slider-image__chip"
              onClick={e => {
                e.stopPropagation();
                onItemClick(mapTrackIndex);
              }}
              label={trans('slider.carousel.map')}
              size="big"
            />
          )}
          {hasVideoChip && (
            <Chip.Choice
              renderLeadingIcon={() => <DSIcon name="smart_display" />}
              className="main-slider-image__chip"
              onClick={e => {
                e.stopPropagation();
                onItemClick(videoTrackIndex);
              }}
              label={trans('slider.carousel.video')}
              size="big"
            />
          )}
        </div>
      )}
    </div>
  );
};

MainSliderItem.propTypes = {
  tracks: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({
          caption: PropTypes.string,
          src: PropTypes.string.isRequired
        })
      ),
      isHidden: PropTypes.bool
    })
  ),
  onItemClick: PropTypes.func.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired
};

export default MainSliderItem;
